/**
 * Every API path this SPA calls, declared once.
 *
 * The two halves of Portal were built in parallel and their route contracts drifted apart.
 * Nothing caught it: a path is a string, so neither compiler sees a mismatch and no test
 * fails — the SPA simply 404s the moment a human loads the page. Declaring the paths here,
 * as data, is what makes the contract checkable. `apps/api/src/routes.test.ts` reads this
 * file and asserts that every entry below corresponds to a route actually registered on the
 * Hono app, so a rename on either side fails the build instead of the browser.
 *
 * Two rules keep that check working:
 *  - Every call site imports from here. A bare `'/api/…'` string anywhere else under
 *    `apps/web/src` fails the same test.
 *  - Each entry keeps `method` and `path` together, in that order. The cross-check reads
 *    them as a pair, and `method` is passed to `apiRequest` rather than restated at the call
 *    site, so a method that drifts from this table cannot go unnoticed either.
 *
 * `:id` in the API's registered pattern and `${issueId}` here are the same thing; the
 * cross-check normalises both to a wildcard segment, so parameter NAMES may differ but the
 * shape may not.
 */
export const API_ROUTES = {
  /*
   * BETTER AUTH'S OWN ENDPOINT, DECLARED HERE ANYWAY.
   *
   * The SPA navigates to this to resume an OAuth authorization after signing somebody in, so it
   * is a path written in this app and it belongs in this table rather than inline — that is the
   * whole rule. It is served by the `/api/auth/*` mount rather than by a route of its own, which
   * the cross-check in `routes.test.ts` accounts for explicitly.
   */
  oauthAuthorize: { method: 'GET', path: () => '/api/auth/oauth2/authorize' },

  me: { method: 'GET', path: () => '/api/me' },
  // Your own display name. Every role — it is your own row, so there is no staff-only variant
  // and no path here that renames anybody else.
  patchMe: { method: 'PATCH', path: () => '/api/me' },
  // Whether YOU have linked your own GitHub account. Every role, and there is no path here
  // that reads anybody else's — see `apps/api/src/routes/github-link.ts`.
  githubLink: { method: 'GET', path: () => '/api/me/github' },

  // The applications you have connected to your account over OAuth — what the profile page
  // lists as MCP connections, and the revoke beside each one.
  mcpConnections: { method: 'GET', path: () => '/api/mcp/connections' },
  revokeMcpConnection: {
    method: 'DELETE',
    path: (id: string) => `/api/mcp/connections/${id}`,
  },
  reviewDeviceAuthorization: {
    method: 'GET',
    path: (userCode: string) => `/api/mcp/device-authorizations/${encodeURIComponent(userCode)}`,
  },
  decideDeviceAuthorization: {
    method: 'POST',
    path: (userCode: string) => `/api/mcp/device-authorizations/${encodeURIComponent(userCode)}`,
  },

  projects: { method: 'GET', path: () => '/api/projects' },
  createProject: { method: 'POST', path: () => '/api/projects' },
  project: { method: 'GET', path: (id: string) => `/api/projects/${id}` },
  patchProject: { method: 'PATCH', path: (id: string) => `/api/projects/${id}` },
  deleteProject: { method: 'DELETE', path: (id: string) => `/api/projects/${id}` },
  resyncProject: { method: 'POST', path: (id: string) => `/api/projects/${id}/resync` },
  classifyProject: { method: 'POST', path: (id: string) => `/api/projects/${id}/classify` },
  reestimateProject: {
    method: 'POST',
    path: (id: string) => `/api/projects/${id}/reestimate`,
  },
  applyEstimates: {
    method: 'POST',
    path: (id: string) => `/api/projects/${id}/apply-estimates`,
  },
  cancelProjectRun: {
    method: 'POST',
    path: (id: string) => `/api/projects/${id}/cancel-run`,
  },

  organizations: { method: 'GET', path: () => '/api/organizations' },
  patchOrganization: { method: 'PATCH', path: (id: string) => `/api/organizations/${id}` },
  deleteOrganization: { method: 'DELETE', path: (id: string) => `/api/organizations/${id}` },

  members: { method: 'GET', path: () => '/api/members' },
  patchMember: { method: 'PATCH', path: (id: string) => `/api/members/${id}` },
  deleteMember: { method: 'DELETE', path: (id: string) => `/api/members/${id}` },

  notifications: { method: 'GET', path: () => '/api/notifications' },
  // Its own route rather than a field on the list: the bell asks for this on a timer, and the
  // list is expensive by comparison.
  unreadCount: { method: 'GET', path: () => '/api/notifications/unread-count' },
  markNotificationRead: { method: 'POST', path: (id: string) => `/api/notifications/${id}/read` },
  markAllNotificationsRead: { method: 'POST', path: () => '/api/notifications/read-all' },

  users: { method: 'GET', path: () => '/api/users' },
  deleteUser: { method: 'DELETE', path: (id: string) => `/api/users/${id}` },
  // Global rather than per-organization: suspension stops sign-in everywhere, which is why it
  // lives on this cross-tenant screen and not on a single client's member list.
  suspendUser: { method: 'POST', path: (id: string) => `/api/users/${id}/suspend` },
  restoreUser: { method: 'POST', path: (id: string) => `/api/users/${id}/restore` },

  // One path for both levels: the body carries `projectId`, null meaning the organisation's
  // own row. A `/policy/:projectId` variant would need a second route for the organisation
  // level with a different shape, and the two would drift.
  // Minting returns the secret once; revoking clears both halves so the old credential stops
  // working immediately.
  mintIngestKey: { method: 'POST', path: (id: string) => `/api/projects/${id}/ingest-key` },
  revokeIngestKey: { method: 'DELETE', path: (id: string) => `/api/projects/${id}/ingest-key` },

  reminderPolicy: { method: 'GET', path: () => '/api/reminders/policy' },
  saveReminderPolicy: { method: 'PATCH', path: () => '/api/reminders/policy' },

  board: { method: 'GET', path: () => '/api/board' },

  issues: { method: 'GET', path: () => '/api/issues' },
  createIssue: { method: 'POST', path: () => '/api/issues' },
  // Many decisions in one request. The ids travel in the BODY, which is why the API's
  // cross-tenant suite still treats this as a by-id route: a foreign id must 404 the whole
  // request rather than come back as a per-row miss inside a 200.
  bulkApproval: { method: 'POST', path: () => '/api/issues/approvals' },
  issue: { method: 'GET', path: (id: string) => `/api/issues/${id}` },
  // The same row addressed the way the browser's URL bar carries it. The org is NOT in this
  // path: the session already names it, and the org slug in the SPA's route is what decides
  // which tenant the session is in before this call is made.
  issueByNumber: {
    method: 'GET',
    path: (projectSlug: string, number: number | string) =>
      `/api/issues/by-number/${encodeURIComponent(projectSlug)}/${number}`,
  },
  issueApproval: { method: 'POST', path: (id: string) => `/api/issues/${id}/approval` },
  issueKind: { method: 'POST', path: (id: string) => `/api/issues/${id}/kind` },
  // Staff-only, and the body carries a BUCKET rather than a column value: the board's columns
  // are derived, so the API writes whatever facts produce the one asked for. Only the three a
  // move can reach — Completed is the GitHub state and Not approved is the approval decision.
  issueMove: { method: 'POST', path: (id: string) => `/api/issues/${id}/move` },
  issueSizing: { method: 'POST', path: (id: string) => `/api/issues/${id}/sizing` },
  issueActualHours: { method: 'POST', path: (id: string) => `/api/issues/${id}/actual-hours` },
  issueNoCharge: { method: 'POST', path: (id: string) => `/api/issues/${id}/no-charge` },
  // Staff-only, both directions: `raise` carries the note, `clear` carries nothing. There is
  // no client-facing dismiss — a client answering is what clears the flag.
  issueAttention: { method: 'POST', path: (id: string) => `/api/issues/${id}/attention` },
  issueGithub: { method: 'PATCH', path: (id: string) => `/api/issues/${id}/github` },
  issueComments: { method: 'GET', path: (id: string) => `/api/issues/${id}/comments` },
  createIssueComment: { method: 'POST', path: (id: string) => `/api/issues/${id}/comments` },
  issueAttachmentList: { method: 'GET', path: (id: string) => `/api/issues/${id}/attachments` },
  issueAttachments: { method: 'POST', path: (id: string) => `/api/issues/${id}/attachments` },
  // The bytes, streamed through the API. Not a signed URL and not a bucket hostname: this is
  // an ordinary session-carrying request, authorised again on every read, so it belongs in
  // this table beside everything else rather than in a link the API hands out.
  issueAttachment: {
    method: 'GET',
    path: (id: string, attachmentId: string) => `/api/issues/${id}/attachments/${attachmentId}`,
  },

  dashboardBuckets: { method: 'GET', path: () => '/api/dashboard/buckets' },
  dashboardSummary: { method: 'GET', path: () => '/api/dashboard/summary' },
  dashboardForecast: { method: 'GET', path: () => '/api/dashboard/forecast' },
  dashboardAttention: { method: 'GET', path: () => '/api/dashboard/attention' },
  dashboardHours: { method: 'GET', path: () => '/api/dashboard/hours' },
  billingStatements: { method: 'GET', path: () => '/api/billing-statements' },
  billingStatement: { method: 'GET', path: (id: string) => `/api/billing-statements/${id}` },
  exportBillingStatement: {
    method: 'GET',
    path: (id: string) => `/api/billing-statements/${id}/export.csv`,
  },

  createInvitation: { method: 'POST', path: () => '/api/invitations' },
  invitations: { method: 'GET', path: () => '/api/invitations' },
  cancelInvitation: { method: 'DELETE', path: (id: string) => `/api/invitations/${id}` },

  submitFeedback: { method: 'POST', path: () => '/api/feedback' },

  timeAuditSummary: {
    method: 'GET',
    path: (month: string) => `/api/time-audit/months/${month}/summary`,
  },
  timeAuditEntries: {
    method: 'GET',
    path: (month: string) => `/api/time-audit/months/${month}/entries`,
  },
  timeAuditEntry: { method: 'GET', path: (id: string) => `/api/time-audit/entries/${id}` },
  suggestTimeAudit: {
    method: 'POST',
    path: (month: string) => `/api/time-audit/months/${month}/suggest`,
  },
  previewTimeAuditDecisions: { method: 'POST', path: () => '/api/time-audit/decisions/preview' },
  recordTimeAuditDecisions: { method: 'POST', path: () => '/api/time-audit/decisions' },
  timeAuditApprovalSnapshotGaps: {
    method: 'GET',
    path: (month: string) => `/api/time-audit/months/${month}/approval-snapshots`,
  },
  previewTimeAuditApprovalSnapshots: {
    method: 'POST',
    path: () => '/api/time-audit/approval-snapshots/preview',
  },
  recordTimeAuditApprovalSnapshots: {
    method: 'POST',
    path: () => '/api/time-audit/approval-snapshots',
  },
  timeAuditAllowance: { method: 'GET', path: () => '/api/time-audit/allowance' },
  recordTimeAuditAllowance: { method: 'POST', path: () => '/api/time-audit/allowance' },
  exportTimeAudit: {
    method: 'GET',
    path: (month: string) => `/api/time-audit/months/${month}/export.csv`,
  },
  previewBillingStatement: {
    method: 'GET',
    path: (month: string) => `/api/time-audit/months/${month}/statement/preview`,
  },
  timeAuditStatementHistory: {
    method: 'GET',
    path: (month: string) => `/api/time-audit/months/${month}/statements`,
  },
  publishBillingStatement: {
    method: 'POST',
    path: (month: string) => `/api/time-audit/months/${month}/statements`,
  },
} as const;
